/**
 * Harper's Approach: the few settings a person can set over a job's
 * instructions, and the record of how an Update was written.
 *
 * A line **Reports on** a template or is Custom. A template (Merged pull
 * requests) is one specific report Harper writes well with no instructions:
 * it fixes what Harper reads and how the post is shaped, and instructions only
 * add to it. A Custom line is its instructions (free text, required). The
 * Approach holds only what code has to enforce or what changes what Harper
 * reads: who the Update is for, how long it is, and how it is split into
 * sections. Each setting is `auto` until a person sets it: Harper follows the
 * instructions when they say something about it, and the template's default
 * when they do not. A value a person set wins over the instructions.
 *
 * One table of words per option, so the Line page, the prompts and the run
 * detail describe the same choice the same way.
 */
export const APPROACH_AUDIENCES = ["managers", "engineers", "customers"];
export const APPROACH_LENGTHS = ["short", "standard", "detailed", "everything"];
export const APPROACH_GROUPINGS = ["theme", "repository", "author", "time", "deploy"];
export const APPROACH_KEYS = ["audience", "length", "groupBy"];
export const AUTO_APPROACH = { audience: "auto", length: "auto", groupBy: "auto" };
/** What `auto` falls back to when the instructions say nothing. */
export const APPROACH_DEFAULTS = {
    audience: "managers",
    length: "standard",
    groupBy: "theme",
};
/**
 * How much fits. `notable` caps the notable items and `alsoNamed` the items
 * named in the "also" line; everything past them is counted. Null is no cap:
 * nothing is cut for length (what the instructions leave out still is).
 * Every length has one lead.
 */
export const LENGTH_BUDGETS = {
    short: { notable: 3, alsoNamed: 5 },
    standard: { notable: 5, alsoNamed: 8 },
    detailed: { notable: 8, alsoNamed: 15 },
    everything: { notable: null, alsoNamed: null },
};
export const APPROACH_SETTING_LABEL = {
    audience: "Audience",
    length: "Length",
    groupBy: "Groups by",
};
export const APPROACH_COPY = {
    audience: {
        managers: { label: "Managers", meaning: "What changed and why it matters. Plain words, no code terms." },
        engineers: {
            label: "Engineers",
            meaning: "Technical detail: code names, migrations, risks to watch. Internal work counts.",
        },
        customers: {
            label: "Customers",
            meaning: "Only what users of the product notice. Internal work is always left out.",
        },
    },
    length: {
        short: { label: "Short", meaning: "The top 3 changes; the rest counted" },
        standard: { label: "Standard", meaning: "The top 5 changes; the rest counted" },
        detailed: { label: "Detailed", meaning: "The top 8 changes; the rest counted" },
        everything: { label: "Everything", meaning: "Every change listed; only what your instructions leave out is cut" },
    },
    groupBy: {
        theme: { label: "Theme", meaning: "Sections by what the change is about." },
        repository: { label: "Repository", meaning: "One section per repository." },
        author: { label: "Author", meaning: "Who shipped what." },
        time: { label: "Time order", meaning: "No sections, in the order it happened." },
        deploy: { label: "Deploy", meaning: "One section per production deploy, only what shipped." },
    },
};
export const APPROACH_AUTO_COPY = {
    label: "Auto",
    meaning: "Follows your instructions, or Harper's default if they don't say.",
};
function oneOf(value, allowed) {
    return typeof value === "string" && allowed.includes(value) ? value : null;
}
/** A stored or sent Approach, with anything unknown read as `auto`. */
export function normalizeApproach(value) {
    const record = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    return {
        audience: oneOf(record.audience, APPROACH_AUDIENCES) ?? "auto",
        length: oneOf(record.length, APPROACH_LENGTHS) ?? "auto",
        groupBy: oneOf(record.groupBy, APPROACH_GROUPINGS) ?? "auto",
    };
}
/** A resolved Approach, with anything unknown read as the default. */
export function normalizeResolvedApproach(value) {
    const record = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    return {
        audience: oneOf(record.audience, APPROACH_AUDIENCES) ?? APPROACH_DEFAULTS.audience,
        length: oneOf(record.length, APPROACH_LENGTHS) ?? APPROACH_DEFAULTS.length,
        groupBy: oneOf(record.groupBy, APPROACH_GROUPINGS) ?? APPROACH_DEFAULTS.groupBy,
    };
}
/** The label of a resolved value ("Standard", "Managers"). */
export function approachValueLabel(key, value) {
    const table = APPROACH_COPY[key];
    return table[value]?.label ?? value;
}
// ---------------------------------------------------------------------------
// Reports on: the template a line writes from
// ---------------------------------------------------------------------------
/**
 * Stored in `harper_jobs.category` (the code's name for it stays `WrittenAs`).
 * Each id is a template: it fixes what Harper reads and how the post is
 * shaped, never when it posts (the line's trigger says that). `other` is
 * Custom, the instructions' own report. Customer copy only prints the labels.
 */
export const WRITTEN_AS = ["merged_prs", "other"];
/** Retired ids a stored row or an older client can still carry, and the template each reads as. */
const RETIRED_WRITTEN_AS = {
    full_list: "merged_prs",
    signal_vs_noise: "merged_prs",
};
export const WRITTEN_AS_SETTING_LABEL = "Reports on";
export const WRITTEN_AS_COPY = {
    merged_prs: {
        label: "Merged pull requests",
        meaning: "Every pull request merged into this Team's repositories since the last post: one line each, under its product area, with who made it.",
    },
    other: {
        label: "Custom",
        meaning: "Harper's house style and your instructions, nothing else.",
    },
};
/** A stored or sent value: a retired id reads as the template that replaced it, anything unknown as `fallback`. */
export function normalizeWrittenAs(value, fallback = "other") {
    if (typeof value === "string" && RETIRED_WRITTEN_AS[value])
        return RETIRED_WRITTEN_AS[value];
    return oneOf(value, WRITTEN_AS) ?? fallback;
}
/** Whether a sent value names a template, retired ones included. */
export function isWrittenAs(value) {
    return typeof value === "string" && (oneOf(value, WRITTEN_AS) !== null || value in RETIRED_WRITTEN_AS);
}
/** Only a Custom line has nothing to write from but its instructions. */
export function writtenAsNeedsInstructions(writtenAs) {
    return writtenAs === "other";
}
/** What an `auto` setting falls back to on each template. */
export function approachDefaultsFor(writtenAs) {
    if (writtenAs === "merged_prs")
        return { audience: "engineers", length: "everything", groupBy: "theme" };
    return APPROACH_DEFAULTS;
}
/**
 * The style switches code acts on: an emoji on each area, and who made each
 * change. Never a setting on the page: the template's default holds unless
 * the instructions say otherwise, and the run records which words decided it.
 */
export const STYLE_KEYS = ["emoji", "authors"];
export const STYLE_LABEL = { emoji: "Emoji", authors: "Authors" };
export function styleDefaultsFor(writtenAs) {
    return { emoji: "on", authors: writtenAs === "merged_prs" ? "on" : "off" };
}
/** A resolved style, with anything unknown read as the template's default. */
export function normalizeResolvedStyle(value, writtenAs) {
    const record = value && typeof value === "object" && !Array.isArray(value) ? value : {};
    const defaults = styleDefaultsFor(writtenAs);
    return {
        emoji: oneOf(record.emoji, ["on", "off"]) ?? defaults.emoji,
        authors: oneOf(record.authors, ["on", "off"]) ?? defaults.authors,
    };
}
//# sourceMappingURL=harperApproach.js.map